import { memo } from 'react';
import moment from 'moment';

import { getByPath } from 'utils/widgets';

import { cn } from '@/shadcn/lib/utils';
import { WidgetData } from '@/components/ui/widget/widget.type';
import { DateView } from '@/components/ui/widget/views/date/date.view.type';
import { TextView } from '@/components/ui/widget/views/text/text.view.type';

import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent
} from '@/shadcn/tooltip';

const DEFAULT_DISPLAY_FORMAT = 'DD/MM/YYYY';

type Props = {
  view: DateView;
  data: WidgetData;
};

const DateViewImpl = memo(({ view, data }: Props) => {
  const {
    path,
    inputFormat,
    format = DEFAULT_DISPLAY_FORMAT,
    isDeadline,
    deadlineWarningBuffer = 0,
    donePath
  } = view;

  const dateStr = getByPath(data, path);
  if (!dateStr) return null;

  const date = moment(dateStr, inputFormat);

  // Deadline functionalities
  let warning = false;
  let daysToDeadline: number | null = null;
  let done: boolean | undefined = undefined;
  let countdownStr = (
    <p>
      <span className="mr-1">✅</span> Done
    </p>
  );
  if (isDeadline) {
    daysToDeadline = date.diff(moment(), 'days');
    const daysToWarning = daysToDeadline - deadlineWarningBuffer;
    done = getByPath<boolean>(data, donePath);
    warning = !done && daysToWarning <= 0;
    if (!done) {
      switch (true) {
        case daysToDeadline === 0:
          countdownStr = (
            <p>
              <span className="mr-1">🚨</span> Due today
            </p>
          );
          break;
        case daysToDeadline > 0:
          countdownStr = (
            <p>
              <span className="mr-1">📅</span> Due in {daysToDeadline} days
            </p>
          );
          break;
        default:
          countdownStr = (
            <p>
              <span className="mr-1">❌</span> {-daysToDeadline} days overdue
            </p>
          );
      }
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-row flex-nowrap items-center overflow-hidden text-left">
            <p
              className={cn(
                'pt-0.5 text-ellipsis',
                warning && 'text-warning min-w-[77px] font-bold'
              )}
            >
              {date.format(format)}
            </p>
            {warning && <DeadlineWarningIcon />}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {isDeadline && countdownStr ? countdownStr : date.fromNow()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

const DeadlineWarningIcon = () => {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      role="img"
      focusable="false"
      aria-hidden="true"
      fill="currentColor"
      className="text-warning ml-2.5 size-4 shrink-1"
    >
      <path d="M11 1C13.2091 1 15 2.79086 15 5V11C15 13.2091 13.2091 15 11 15H5C2.79086 15 1 13.2091 1 11V5C1 2.79086 2.79086 1 5 1H11ZM13.5 6H2.5V11C2.5 12.3807 3.61929 13.5 5 13.5H11C12.3807 13.5 13.5 12.3807 13.5 11V6Z" />
    </svg>
  );
};

// sort defaults to text on path (text is ok assuming iso dates)
const getSortConfig = (view: DateView): TextView => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
// but using a date interpreter in case a specific display format has been set
const getFilterConfig = (view: DateView) => ({
  type: 'dateRange',
  path: view.path,
  inputFormat: view.inputFormat,
  format: view.format || DEFAULT_DISPLAY_FORMAT
});

// width defaults to 'medium'
const getDefaultWidth = () => 'medium';

// stringifies data at path
const getStringifyFunction = (view: DateView) => {
  const { path, inputFormat, format = DEFAULT_DISPLAY_FORMAT } = view;

  return (data: WidgetData): string => {
    const dateStr = getByPath(data, path);
    if (!dateStr) return '';

    const date = moment(dateStr, inputFormat);
    return date.format(format);
  };
};

export const dateViewProvider = {
  component: DateViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
